import {
  RestrictedDataRedactionState,
  RestrictedDataType,
} from '../models/restricted-data';

// The BFF restores model output against its own un-namespaced mask ledger
// (FIRSTNAME0…); a colliding client mask would restore to the server's value.
export const CLIENT_MASK_NAMESPACE = 'MX';

// Placeholders are UPPERCASE+number (<MXSIN1>) so echoed masks match the de-anonymizer tokenizers
const RESTRICTED_DATA_REDACTION_LABELS: {
  readonly [type: string]: string | undefined;
} = {
  SIN: '[SIN removed]',
  SSN: '[SSN removed]',
  CREDITCARD: '[credit card removed]',
  BANKACCOUNT: '[bank account removed]',
};

// Maximal 6+ digit runs with single space/dash separators; the lookarounds
// stop sub-runs of a longer separated number from matching. No upper bound:
// adjacent numbers joined by a single separator merge into one candidate and
// are split back apart by the space-segment fallback in redactRestrictedData.
const DIGIT_RUN_CANDIDATE = /(?<!\d[ -]?)\d(?:[ -]?\d){5,}(?![ -]?\d)/g;

const CREDIT_CARD_MIN_DIGITS = 13;
const CREDIT_CARD_MAX_DIGITS = 19;
const SIN_DIGIT_COUNT = 9;
const KEYWORDED_ACCOUNT_MIN_DIGITS = 7;
const KEYWORDED_ACCOUNT_MAX_DIGITS = 12;

// Real SIN/card/account groupings are <=4 space-separated segments; a longer
// run is a merged/attacker blob. Cap the fallback split so it can't turn an
// inbound email body into an O(N^3) span scan (client-side DoS).
const MAX_FALLBACK_SEGMENTS = 64;

const BANK_ACCOUNT_TRANSIT_FORMAT = /^\d{5}[ -]\d{3}[ -]\d{7,12}$/;
const SSN_GROUPED_FORMAT = /^\d{3}[ -]\d{2}[ -]\d{4}$/;
const SIN_GROUPED_FORMAT = /^\d{3}[ -]\d{3}[ -]\d{3}$/;

const ACCOUNT_KEYWORD = /\b(?:account|acct|a\/c)\b/i;
const ACCOUNT_KEYWORD_LOOKBEHIND_CHARS = 25;

const ALL_DIGITS = /^\d+$/;
const DIGIT_SEPARATORS = /[ -]/g;
const NUMBERED_MASK = /^([A-Z]+)\d+$/;
// Lookbehind pins the run's start so the match can't restart at every digit
// (that retry is what makes a plain /\d+$/ back-track super-linearly).
const TRAILING_NUMBER = /(?<!\d)\d+$/;

export function createRedactionState(): RestrictedDataRedactionState {
  return {
    placeholderByNormalizedValue: new Map<string, string>(),
    nextNumberByType: new Map<RestrictedDataType, number>(),
  };
}

// Luhn (mod-10) checksum: real card/SIN numbers end in a check digit that
// makes this sum divisible by 10, so it filters out typos and random runs.
export function passesLuhnChecksum(digits: string): boolean {
  if (!ALL_DIGITS.test(digits)) return false;
  let sum = 0;
  let shouldDouble = false;
  for (let index = digits.length - 1; index >= 0; index--) {
    let digit = (digits.codePointAt(index) ?? 0) - 48;
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

/**
 * One-way redaction of restricted financial identifiers into numbered
 * placeholders like `<MXSIN1>`. Unlike Tier 2 anonymization these are never
 * reversed. Idempotent: placeholders contain no long digit runs.
 */
export function redactRestrictedData(
  text: string,
  state: RestrictedDataRedactionState,
): string {
  if (!text) return text;

  let result = '';
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = DIGIT_RUN_CANDIDATE.exec(text)) !== null) {
    const candidate = match[0];
    const replacement = redactCandidate(candidate, text, match.index, state);
    if (!replacement) continue;

    result += text.slice(lastIndex, match.index) + replacement;
    lastIndex = match.index + candidate.length;
  }
  return result + text.slice(lastIndex);
}

function redactCandidate(
  candidate: string,
  text: string,
  matchIndex: number,
  state: RestrictedDataRedactionState,
): string | undefined {
  const type = classifyRestrictedData(candidate, text, matchIndex);
  if (type) {
    return assignPlaceholder(
      type,
      candidate.replace(DIGIT_SEPARATORS, ''),
      state,
    );
  }
  return redactSpaceSeparatedSegments(candidate, text, matchIndex, state);
}

/**
 * Fallback for candidate runs that classify as nothing: adjacent numbers
 * joined by single spaces merge into one candidate (e.g. two card numbers
 * pasted side by side), so the longest classifiable space-delimited segment
 * spans are redacted individually. Dash-joined runs stay atomic — dashes
 * group a single number, and splitting them would redact sub-groups of
 * longer reference numbers.
 */
function redactSpaceSeparatedSegments(
  candidate: string,
  text: string,
  matchIndex: number,
  state: RestrictedDataRedactionState,
): string | undefined {
  const segments = candidate.split(' ');
  if (segments.length < 2 || segments.length > MAX_FALLBACK_SEGMENTS) {
    return undefined;
  }

  const pieces: string[] = [];
  let redactedAny = false;
  let segmentOffset = 0;
  let segmentIndex = 0;
  while (segmentIndex < segments.length) {
    const span = findLongestClassifiedSpan(
      segments,
      segmentIndex,
      text,
      matchIndex + segmentOffset,
    );
    if (span) {
      pieces.push(
        assignPlaceholder(
          span.type,
          span.value.replace(DIGIT_SEPARATORS, ''),
          state,
        ),
      );
      redactedAny = true;
      segmentOffset += span.value.length + 1;
      segmentIndex = span.endIndex + 1;
    } else {
      pieces.push(segments[segmentIndex]);
      segmentOffset += segments[segmentIndex].length + 1;
      segmentIndex++;
    }
  }
  return redactedAny ? pieces.join(' ') : undefined;
}

function findLongestClassifiedSpan(
  segments: string[],
  startIndex: number,
  text: string,
  spanStartInText: number,
): { type: RestrictedDataType; value: string; endIndex: number } | undefined {
  for (let endIndex = segments.length - 1; endIndex >= startIndex; endIndex--) {
    const value = segments.slice(startIndex, endIndex + 1).join(' ');
    const type = classifyRestrictedData(value, text, spanStartInText);
    if (type) return { type, value, endIndex };
  }
  return undefined;
}

const ANY_MASK = /<([A-Z]+\d+)>/gi;

const UNNAMESPACED_MASK = new RegExp(
  String.raw`<(?!${CLIENT_MASK_NAMESPACE})([A-Z]+\d+)>`,
  'g',
);

export function namespaceClientMasks(text: string): string {
  if (!text) return text;
  return text.replace(UNNAMESPACED_MASK, `<${CLIENT_MASK_NAMESPACE}$1>`);
}

// Accepts both `MXSIN` (current) and bare `SIN` (masks saved before namespacing).
function stripClientMaskNamespace(type: string): string {
  return type.startsWith(CLIENT_MASK_NAMESPACE)
    ? type.slice(CLIENT_MASK_NAMESPACE.length)
    : type;
}

/** Label for a Tier 1 mask (`MXSIN1` → `[SIN removed]`); undefined for other
 * mask types so callers can fall through. */
export function getRestrictedDataRedactionLabel(
  maskInner: string,
): string | undefined {
  const typeMatch = NUMBERED_MASK.exec(maskInner);
  if (!typeMatch) return undefined;
  return RESTRICTED_DATA_REDACTION_LABELS[
    stripClientMaskNamespace(typeMatch[1])
  ];
}

/** True for Tier 1 restricted-data types, with or without a trailing number
 * or namespace (`SIN`, `SIN1`, `MXSIN1`). */
export function isRestrictedDataMaskType(maskType: string): boolean {
  const typeWithoutNumber = maskType.toUpperCase().replace(TRAILING_NUMBER, '');
  return (
    RESTRICTED_DATA_REDACTION_LABELS[
      stripClientMaskNamespace(typeWithoutNumber)
    ] !== undefined
  );
}

/** True when a value still carries a Tier 1 placeholder (`<MXSIN1>`), so
 * callers can drop model output that echoed a redacted number back. */
export function containsRestrictedDataMask(value: string): boolean {
  if (!value) return false;
  ANY_MASK.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = ANY_MASK.exec(value)) !== null) {
    if (isRestrictedDataMaskType(match[1])) return true;
  }
  return false;
}

function classifyRestrictedData(
  candidate: string,
  text: string,
  matchIndex: number,
): RestrictedDataType | undefined {
  const digitsOnly = candidate.replace(/[ -]/g, '');

  if (isCreditCard(digitsOnly)) return 'CREDITCARD';
  if (BANK_ACCOUNT_TRANSIT_FORMAT.test(candidate)) return 'BANKACCOUNT';
  if (SSN_GROUPED_FORMAT.test(candidate)) return 'SSN';
  if (isSin(candidate, digitsOnly)) return 'SIN';
  if (isKeywordedAccount(digitsOnly, text, matchIndex)) return 'BANKACCOUNT';

  return undefined;
}

function isCreditCard(digitsOnly: string): boolean {
  return (
    digitsOnly.length >= CREDIT_CARD_MIN_DIGITS &&
    digitsOnly.length <= CREDIT_CARD_MAX_DIGITS &&
    passesLuhnChecksum(digitsOnly)
  );
}

// 3-3-3 grouping is a strong enough SIN signal to strip regardless of Luhn; a
// bare 9-digit run must be Luhn-valid so invoice/reference numbers survive.
function isSin(candidate: string, digitsOnly: string): boolean {
  if (SIN_GROUPED_FORMAT.test(candidate)) return true;
  return (
    digitsOnly.length === SIN_DIGIT_COUNT &&
    candidate === digitsOnly &&
    passesLuhnChecksum(digitsOnly)
  );
}

// Standalone account numbers redact only when an account keyword precedes them
function isKeywordedAccount(
  digitsOnly: string,
  text: string,
  matchIndex: number,
): boolean {
  if (
    digitsOnly.length < KEYWORDED_ACCOUNT_MIN_DIGITS ||
    digitsOnly.length > KEYWORDED_ACCOUNT_MAX_DIGITS
  ) {
    return false;
  }
  const precedingText = text.slice(
    Math.max(0, matchIndex - ACCOUNT_KEYWORD_LOOKBEHIND_CHARS),
    matchIndex,
  );
  return ACCOUNT_KEYWORD.test(precedingText);
}

function assignPlaceholder(
  type: RestrictedDataType,
  digitsOnly: string,
  state: RestrictedDataRedactionState,
): string {
  const normalizedKey = `${type}:${digitsOnly}`;
  const existingPlaceholder =
    state.placeholderByNormalizedValue.get(normalizedKey);
  if (existingPlaceholder) return existingPlaceholder;

  const nextNumber = state.nextNumberByType.get(type) ?? 1;
  const placeholder = `<${CLIENT_MASK_NAMESPACE}${type}${nextNumber}>`;
  state.nextNumberByType.set(type, nextNumber + 1);
  state.placeholderByNormalizedValue.set(normalizedKey, placeholder);
  return placeholder;
}
